import React, { useEffect } from "react";
import { Box, Loader, LoadingOverlay, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { Title } from "../../stories/Title/Title";
import { UserType, UserRole, SelectInputRef } from "@jasper/shared";
import { User, Order } from "@jasper/shared/types/interfaces";
import { useQuery } from "@apollo/client";
import {
  GET_ALL_PRACTITIONERS,
  GET_PRACTITIONERS_FROM_USER_GROUP,
} from "@jasper/shared/gql/users";
import { patientSexChoices } from "@jasper/shared/types/enums";
import { Controller, useFormContext } from "react-hook-form";

export const Patient: React.FC<{
  order: Order;
  user: User;
}> = ({ order, user }) => {
  const { data: getPractitioners, loading: getPractitionersLoading } = useQuery(
    GET_PRACTITIONERS_FROM_USER_GROUP,
    {
      fetchPolicy: "network-only",
    }
  );

  const { data: getAllDentist, loading: getAllDentistLoading } = useQuery(
    GET_ALL_PRACTITIONERS,
    {
      variables: {
        where: {
          role: {
            in: [UserRole.USER, UserRole.ADMIN, UserRole.STAFF],
          },
          type: {
            in: [UserType.DENTIST, UserType.JASPER],
          },
          isManager: {
            equals: false,
          },
        },
      },
      fetchPolicy: "network-only",
    }
  );

  const getPractitionersOptions = () => {
    return user?.role === UserRole.ADMIN
      ? (getAllDentist?.getManyUsers ?? [])
          .filter(usr =>
            (usr?.userGroup?.groupMembers ?? []).length > 1
              ? !usr?.isManager
              : true
          )
          .map(usr => ({
            label: `Dr. ${usr.firstName} ${usr.lastName} - ${usr.userGroup?.clinicNumber}`,
            value: usr.id,
          }))
      : ((getPractitioners?.getManagedUserGroupOfUser?.groupMembers ?? [])
          ?.filter(groupMember => !groupMember.isManager)
          ?.map(groupMember => ({
            label: `Dr. ${groupMember.firstName} ${groupMember.lastName}`,
            value: groupMember.id,
          })) ?? []);
  };

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  useEffect(() => {
    if (order?.patient && order?.user) {
      setValue("firstName", order.patient.firstName);
      setValue("lastName", order.patient.lastName);
      setValue(
        "birthDate",
        order.patient.birthdate ? new Date(order.patient.birthdate) : null
      );
      setValue("sex", order.patient.sex);
      setValue("doctorId", order.user.id);
    }
  }, [order]);

  return (
    <Box pt="lg">
      <LoadingOverlay visible={getPractitionersLoading || getAllDentistLoading}>
        <Loader />
      </LoadingOverlay>
      <Title
        variant="primary"
        size={4}
        title="Produit commandé"
      />
      <Text>Prothèse amovible partielle</Text>
      <Box my="md">
        <Title
          variant="primary"
          size={4}
          title="Cette commande est réalisée par :"
        />
        <SelectInputRef
          label="Docteur"
          name="doctorId"
          data={getPractitionersOptions()}
          errors={errors}
          control={control}
          required
          errorMessage="Le docteur est requis"
          searchable
          clearable
          placeholder="Selectionnez un docteur"
        />
      </Box>
      <Title
        mb="sm"
        size={4}
        title="Patient :"
      />
      <TextInput
        {...register("firstName", { required: "Le prénom est requis" })}
        label="Prénom"
        placeholder="Prénom"
        size="sm"
        data-testid="input-patient-firstname"
      />
      {errors.firstName && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.firstName.message as string}
        </span>
      )}
      <TextInput
        {...register("lastName", { required: "Le nom est requis" })}
        label="Nom"
        placeholder="Nom"
        size="sm"
        data-testid="input-patient-lastname"
      />
      {errors.lastName && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.lastName.message as string}
        </span>
      )}
      <Controller
        control={control}
        name="birthDate"
        render={({ field }) => (
          <DateInput
            {...field}
            placeholder="Date de naissance du patient"
            valueFormat="DD/MM/YYYY"
            label="Date de naissance"
            locale="fr"
            data-testid="input-patient-birthdate"
          />
        )}
      />
      <SelectInputRef
        required={false}
        errors={errors}
        label="Sexe"
        name="sex"
        data={patientSexChoices}
        control={control}
        placeholder="Sexe du patient"
      />
    </Box>
  );
};
